import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, Loader2, Edit, Sun, Zap, BatteryCharging, Image as ImageIcon } from "lucide-react";

const API = "/api";
const CATEGORIES = ["Pannelli", "Inverter", "Batterie"];
const CAT_ICONS = { Pannelli: Sun, Inverter: Zap, Batterie: BatteryCharging };
const CAT_COLORS = {
  Pannelli: "emerald",
  Inverter: "blue",
  Batterie: "amber",
};

const AdminProducts = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", logo_url: "", category: "Pannelli", order: 0 });
  const [saving, setSaving] = useState(false);

  const fetchBrands = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/brands`);
      setBrands(res.data);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const startNew = (cat) => {
    setEditing("new");
    setForm({ name: "", logo_url: "", category: cat || "Pannelli", order: 0 });
  };

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({ name: b.name, logo_url: b.logo_url, category: b.category, order: b.order || 0 });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.logo_url.trim()) {
      toast.error("Nome e URL logo sono obbligatori");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/brands`, form);
        toast.success("Brand aggiunto!");
      } else {
        await axios.put(`${API}/admin/brands/${editing}`, form);
        toast.success("Brand aggiornato!");
      }
      setEditing(null);
      fetchBrands();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo brand?")) return;
    try {
      await axios.delete(`${API}/admin/brands/${id}`);
      toast.success("Brand eliminato");
      fetchBrands();
    } catch { toast.error("Errore eliminazione"); }
  };

  if (editing) {
    return (
      <div data-testid="brand-editor">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-600 text-sm font-medium">
            &larr; Indietro
          </button>
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            {editing === "new" ? "Nuovo Brand" : "Modifica Brand"}
          </h2>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6 max-w-lg space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Nome Brand *</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Es. SunPower" className="h-10 rounded-xl" data-testid="brand-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">URL Logo *</label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
              placeholder="https://example.com/logo.png" className="h-10 rounded-xl" data-testid="brand-logo-input" />
            {form.logo_url && (
              <div className="mt-2 bg-slate-50 rounded-lg p-3 flex items-center justify-center h-20">
                <img src={form.logo_url} alt="Preview" className="max-h-14 max-w-[140px] object-contain"
                  onError={(e) => { e.target.style.display = "none"; }} />
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Categoria</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="flex h-10 w-full rounded-xl border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="brand-category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Ordine</label>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
              className="h-10 rounded-xl w-24" data-testid="brand-order-input" />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <button onClick={() => setEditing(null)}
              className="text-sm font-semibold text-slate-500 hover:text-slate-700 px-4 py-2">Annulla</button>
            <button onClick={handleSave} disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50"
              data-testid="brand-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editing === "new" ? "Aggiungi" : "Salva"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-brands">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
          Marchi Partner
        </h2>
        <button onClick={() => startNew()} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-5 py-2.5 text-sm btn-scale flex items-center gap-2"
          data-testid="brand-create-btn">
          <Plus className="w-4 h-4" /> Nuovo Brand
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
      ) : (
        <div className="space-y-8">
          {CATEGORIES.map((cat) => {
            const items = brands.filter((b) => b.category === cat);
            const Icon = CAT_ICONS[cat];
            const color = CAT_COLORS[cat];
            return (
              <div key={cat} data-testid={`admin-brand-section-${cat.toLowerCase()}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                      <Icon className={`w-4 h-4 text-${color}-600`} />
                    </div>
                    <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {cat} ({items.length})
                    </h3>
                  </div>
                  <button onClick={() => startNew(cat)}
                    className={`text-xs font-semibold text-${color}-600 hover:text-${color}-700 flex items-center gap-1`}
                    data-testid={`brand-add-${cat.toLowerCase()}`}>
                    <Plus className="w-3.5 h-3.5" /> Aggiungi
                  </button>
                </div>

                {items.length === 0 ? (
                  <div className="py-6 text-center text-slate-400 text-xs bg-white rounded-xl border border-slate-100">
                    Nessun brand — clicca "Aggiungi" per inserirne uno
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((b) => (
                      <div key={b.id} className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
                        data-testid={`brand-item-${b.id}`}>
                        <div className="w-14 h-14 bg-slate-50 rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                          {b.logo_url ? (
                            <img src={b.logo_url} alt={b.name} className="max-h-10 max-w-[48px] object-contain"
                              onError={(e) => { e.target.parentElement.innerHTML = '<span class="text-slate-300 text-xs">No img</span>'; }} />
                          ) : (
                            <ImageIcon className="w-5 h-5 text-slate-300" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900 truncate">{b.name}</div>
                          <div className="text-[10px] text-slate-400">Ordine: {b.order}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button onClick={() => startEdit(b)}
                            className="text-blue-500 hover:text-blue-600 p-1.5 rounded-lg hover:bg-blue-50"
                            data-testid={`brand-edit-${b.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(b.id)}
                            className="text-red-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50"
                            data-testid={`brand-delete-${b.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
